import { useState, useEffect, useCallback, useMemo } from "react";
import { Typography } from "@mui/material";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { useForm } from "react-hook-form";
import type { SectorAdminRow } from "../../types";
import { SectorDropdown } from "../Forms/SectorDropdown";
import { RemoveSectorsConfirmation } from "../RemoveSectorsConfirmation";
import { UserManagementDialogShell } from "../UserManagementDialogShell";
import { WouldUserHaveNoAccessAfterChange } from "../../utils/userAccessUtils";
import { useSelector } from "react-redux";
import { RootState, UserSlice } from "store";
import { SelectUserSectors } from "store/slices/userSearchSlice";
import { UsePermissionRefresh } from "lib/hooks/useSectorsAndPermissions";

export interface EditSectorAdminDialogProps {
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	selectedRow: SectorAdminRow | null;
}

export function EditSectorAdminDialog({
	open,
	onClose,
	onSuccess,
	selectedRow,
}: EditSectorAdminDialogProps) {
	const api = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const permissions = UseHasPermission();
	const { refreshPermissions } = UsePermissionRefresh();
	const currentUser = useSelector(UserSlice.selectors.selectUser);
	const currentUserId = currentUser?.id || "";

	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>("");
	const [confirmationChecked, setConfirmationChecked] = useState(false);
	const [initialFormSectorIdsSet, setInitialFormSectorIdsSet] = useState<Set<string>>(() => new Set());

	type sectorFormData = { sectors: { id: string; name: string }[] };
	const { control, watch, setValue, reset, trigger } = useForm<sectorFormData>({
		defaultValues: { sectors: [] },
		mode: "onChange",
		reValidateMode: "onChange",
	});
	const selectedSectors = watch("sectors");

	const fullUserSectors = useSelector((state: RootState) =>
		selectedRow ? SelectUserSectors(state, selectedRow.userId) : null
	);

	const selectedSectorIdsSet = useMemo(() => {
		if (!selectedSectors || !Array.isArray(selectedSectors)) return new Set<string>();
		return new Set(selectedSectors.map((s) => s.id).filter(Boolean));
	}, [selectedSectors]);

	const removedSectorIds = useMemo(
		() => [...initialFormSectorIdsSet].filter((id) => !selectedSectorIdsSet.has(id)),
		[initialFormSectorIdsSet, selectedSectorIdsSet]
	);

	const hasSectorChange = useMemo(
		() =>
			removedSectorIds.length > 0 ||
			[...selectedSectorIdsSet].some((id) => !initialFormSectorIdsSet.has(id)),
		[initialFormSectorIdsSet, selectedSectorIdsSet, removedSectorIds.length]
	);

	const wouldHaveNoSectors = useMemo(
		() =>
			WouldUserHaveNoAccessAfterChange(
				fullUserSectors?.sectorIds ?? null,
				removedSectorIds,
				selectedSectorIdsSet.size
			),
		[fullUserSectors?.sectorIds, removedSectorIds, selectedSectorIdsSet.size]
	);

	const isRemovingSectors = useMemo(
		() =>
			initialFormSectorIdsSet.size > 0 &&
			[...initialFormSectorIdsSet].some((id) => !selectedSectorIdsSet.has(id)),
		[initialFormSectorIdsSet, selectedSectorIdsSet]
	);

	const removedSectorNames = useMemo(() => {
		if (!selectedRow?.sectorIds?.length || !selectedRow?.sectors) return [];
		const names: string[] = [];
		initialFormSectorIdsSet.forEach((id) => {
			if (selectedSectorIdsSet.has(id)) return;
			const index = selectedRow.sectorIds.indexOf(id);
			if (index >= 0 && selectedRow.sectors[index]) names.push(selectedRow.sectors[index]);
		});
		return names.sort((a, b) => a.localeCompare(b));
	}, [selectedRow?.sectorIds, selectedRow?.sectors, initialFormSectorIdsSet, selectedSectorIdsSet]);

	useEffect(() => {
		if (open && selectedRow) {
			const currentSectors = selectedRow.sectorIds
				.map((sectorId, index) => {
					const sectorName = selectedRow.sectors[index];
					if (sectorId && sectorName) return { id: sectorId, name: sectorName };
					return null;
				})
				.filter((s): s is { id: string; name: string } => s !== null);
			reset({ sectors: currentSectors });
			setInitialFormSectorIdsSet(new Set(currentSectors.map((s) => s.id).filter(Boolean)));
			setConfirmationChecked(false);
			setError("");
		} else if (!open) {
			reset({ sectors: [] });
			setInitialFormSectorIdsSet(new Set());
			setConfirmationChecked(false);
			setError("");
		}
	}, [open, selectedRow, reset]);

	useEffect(() => {
		if (!isRemovingSectors && !wouldHaveNoSectors) setConfirmationChecked(false);
	}, [isRemovingSectors, wouldHaveNoSectors]);

	const isFormValid = useMemo(() => {
		if (!selectedSectors || !Array.isArray(selectedSectors)) return false;
		if ((isRemovingSectors || wouldHaveNoSectors) && !confirmationChecked) return false;
		return true;
	}, [selectedSectors, isRemovingSectors, wouldHaveNoSectors, confirmationChecked]);

	const handleSave = useCallback(async () => {
		if (!selectedRow) return;
		const isValid = await trigger("sectors");
		if (!isValid && selectedSectors?.length > 0) return;
		if (!isFormValid) return;
		try {
			setSaving(true);
			setError("");
			const selectedIds = new Set((selectedSectors ?? []).map((s) => s.id).filter(Boolean));
			const initialIds = initialFormSectorIdsSet;
			const sectorIdsToAdd = [...selectedIds].filter((id) => !initialIds.has(id));
			const sectorIdsToRemove = [...initialIds].filter((id) => !selectedIds.has(id));
			const request = {
				userModel: {
					businessUser: {
						id: selectedRow.userId,
						name: selectedRow.name,
						contact: { name: selectedRow.name, email: selectedRow.email },
					},
				},
				sectorIdsToAdd,
				sectorIdsToRemove,
			};
			await api.AddSectorAdmin(request);
			if (selectedRow.userId === currentUserId) await refreshPermissions();
			snack.open(result.Ok("Sector admin updated successfully"));
			onSuccess?.();
			onClose();
		} catch (err: any) {
			const errorMsg =
				err?.response?.data?.errorList?.[0]?.message ||
				err?.response?.data?.message ||
				err?.message ||
				"Failed to update sector admin";
			setError(errorMsg);
			snack.open(result.Err(errorMsg));
		} finally {
			setSaving(false);
		}
	}, [
		isFormValid,
		selectedRow,
		selectedSectors,
		api,
		snack,
		onClose,
		onSuccess,
		currentUserId,
		refreshPermissions,
		trigger,
	]);

	if (!permissions.isSectorAdmin || !selectedRow) return null;

	return (
		<UserManagementDialogShell
			open={open}
			onClose={onClose}
			title="Edit Sector Admin Access"
			subTitle={
				<Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 0.5, textAlign: "left" }}>
					Admin: {selectedRow.name}
				</Typography>
			}
			maxWidth="xs"
			loading={loading}
			error={error}
			primaryButton={{
				label: wouldHaveNoSectors ? "Delete" : "Save",
				onClick: handleSave,
				disabled: !isFormValid || saving || selectedRow?.userId === currentUserId || !hasSectorChange,
				loading: saving,
				destructive: wouldHaveNoSectors,
			}}
		>
			<SectorDropdown
				control={control}
				name="sectors"
				watch={watch}
				setValue={setValue}
				trigger={trigger}
				disabled={loading || saving || selectedRow?.userId === currentUserId}
				required={false}
			/>
			{(isRemovingSectors || wouldHaveNoSectors) && (
				<RemoveSectorsConfirmation
					checked={confirmationChecked}
					onCheckedChange={setConfirmationChecked}
					wouldLoseMcc={wouldHaveNoSectors}
					userName={selectedRow.name ?? ""}
					removedSectorNames={removedSectorNames}
					showDivider={true}
				/>
			)}
		</UserManagementDialogShell>
	);
}
